import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, LayoutGrid, ListOrdered, Loader2, Shuffle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, authed, errMsg } from "@/lib/api";

const ReorderDialog = ({ open, setOpen, catId, products, onSaved }) => {
  const [list, setList] = useState(products);
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setList(products); }, [open, products]);

  const moveTo = (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    const n = [...list];
    const [x] = n.splice(from, 1);
    n.splice(to, 0, x);
    setList(n);
  };
  const save = async () => {
    setSaving(true);
    try { await api.post(`/categories/${catId}/reorder`, { ids: list.map((p) => p.id) }); toast.success("New order saved"); setOpen(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[var(--cream)] max-w-xl" data-testid="reorder-dialog">
        <DialogHeader><DialogTitle>Reorder products</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--muted-ink)]">Drag rows, or use the arrows. Each product moves together with its 2 pages.</p>
        <div className="max-h-[60vh] overflow-y-auto border border-[var(--line)]">
          {list.map((p, i) => (
            <div key={p.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => { e.preventDefault(); if (drag !== null && drag !== i) { moveTo(drag, i); setDrag(i); } }}
              onDragEnd={() => setDrag(null)} data-testid={`reorder-row-${i}`}
              className={`flex items-center gap-3 px-3 py-2 border-b border-[var(--line)] bg-[var(--cream)] cursor-grab transition-opacity ${drag === i ? "opacity-40" : ""}`}>
              <GripVertical size={14} className="text-[var(--muted-ink)] shrink-0" />
              <span className="w-6 text-xs font-bold text-[var(--muted-ink)]">{i + 1}</span>
              <img src={authed(`/products/${p.id}/preview/0.png?v=${encodeURIComponent(p.updated_at)}`)} alt="" className="w-16 aspect-[16/9] object-cover bg-[var(--line)]" />
              <span className="flex-1 text-sm"><b className="text-xs mr-2">{p.code}</b>{p.name}</span>
              <button disabled={i === 0} onClick={() => moveTo(i, i - 1)} className="disabled:opacity-20 p-1" data-testid={`reorder-up-${i}`}><ArrowUp size={13} /></button>
              <button disabled={i === list.length - 1} onClick={() => moveTo(i, i + 1)} className="disabled:opacity-20 p-1" data-testid={`reorder-down-${i}`}><ArrowDown size={13} /></button>
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} data-testid="reorder-save-button">{saving && <Loader2 size={14} className="mr-2 animate-spin" />}Save order</Button>
      </DialogContent>
    </Dialog>
  );
};

const Confirm = ({ trigger, title, text, action, onConfirm, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent className="bg-[var(--cream)]">
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{text}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onConfirm} data-testid={testid}>{action}</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ArrangeBar = ({ catId, products, onChange, relayout }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [rl, setRl] = useState(relayout || {});
  useEffect(() => {
    if (rl.status !== "running") return;
    const t = setInterval(async () => {
      const { data } = await api.get(`/categories/${catId}`);
      setRl(data.relayout || {});
      if (data.relayout?.status !== "running") { toast.success("Photos re-arranged for the new layout"); onChange(); }
    }, 3000);
    return () => clearInterval(t);
  }, [rl.status, catId]); // eslint-disable-line react-hooks/exhaustive-deps
  const relayoutAll = async () => {
    try { const { data } = await api.post(`/categories/${catId}/relayout`); setRl(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (key, url, done) => {
    setBusy(key);
    try { const { data } = await api.post(url); done(data); onChange(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const n = products.length;
  return (
    <div className="flex flex-wrap items-center gap-2 py-4 border-b border-[var(--line)]" data-testid="arrange-bar">
      <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)] mr-2">Arrange</p>
      <Button size="sm" variant="outline" disabled={n < 2} onClick={() => setOpen(true)} data-testid="reorder-button"><ListOrdered size={13} className="mr-1" />Reorder</Button>
      <Confirm title="Jumble the order?" text={`All ${n} products will be placed in a random order.`} action="Jumble" testid="confirm-jumble"
        onConfirm={() => run("jumble", `/categories/${catId}/shuffle`, () => toast.success("Order jumbled"))}
        trigger={<Button size="sm" variant="outline" disabled={n < 2 || !!busy} data-testid="jumble-button">{busy === "jumble" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Shuffle size={13} className="mr-1" />}Jumble</Button>} />
      <Confirm title="Rename every product?" text={`AI gives all ${n} products brand-new names (codes, prices and photos stay the same).`} action="Rename all" testid="confirm-rename-all"
        onConfirm={() => run("rename", `/categories/${catId}/rename-all`, (d) => toast.success(`Renamed ${d.renamed.length} products`, { description: d.renamed.slice(0, 4).map((r) => `${r.old} → ${r.new}`).join(" · ") }))}
        trigger={<Button size="sm" variant="outline" disabled={!n || !!busy} data-testid="rename-all-button">{busy === "rename" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />}Rename all</Button>} />
      <Confirm title="Re-arrange photos with AI?" text={`AI picks and crops photos again for all ${n} products so they suit this catalog's layout. Manual crops will be replaced.`} action="Re-arrange" testid="confirm-relayout"
        onConfirm={relayoutAll}
        trigger={<Button size="sm" variant="outline" disabled={!n || rl.status === "running"} data-testid="relayout-button">{rl.status === "running" ? <><Loader2 size={13} className="mr-1 animate-spin" />Re-arranging {rl.current}/{rl.total}</> : <><LayoutGrid size={13} className="mr-1" />AI re-arrange photos</>}</Button>} />
      <ReorderDialog open={open} setOpen={setOpen} catId={catId} products={products} onSaved={onChange} />
    </div>
  );
};
